import { useEffect, type ReactNode } from 'react';
import { appConfig } from '../config/appConfig';
import { ClarityTopicNav } from '../components/ClarityTopicNav';
import { clarityFramingQuestions } from '../lib/clarityFramingQuestions';

type TooltipTermProps = {
  term: string;
  title: string;
  href: string;
  children: ReactNode;
};

function TooltipTerm({ term, title, href, children }: TooltipTermProps) {
  return (
    <span className="clarity-tooltip-term" tabIndex={0}>
      {term}
      <span className="clarity-tooltip-card" role="tooltip">
        <strong>{title}</strong>
        {children}
        <a href={href} target="_blank" rel="noopener noreferrer">
          Read more ↗
        </a>
      </span>
    </span>
  );
}

type SourceTooltipProps = {
  label: string;
  href: string;
  definition: ReactNode;
};

function SourceWithTooltip({ label, href, definition }: SourceTooltipProps) {
  return (
    <div className="clarity-source-tooltip">
      <a className="clarity-source-link" href={href} target="_blank" rel="noopener noreferrer">
        <span>{label}</span>
        <span aria-hidden="true">↗</span>
      </a>
      <span className="clarity-tooltip-card" role="tooltip">
        <strong>Brief definition</strong>
        {definition}
        <a href={href} target="_blank" rel="noopener noreferrer">
          Read more ↗
        </a>
      </span>
    </div>
  );
}

const projectPillars = [
  {
    title: 'Centralization of Executive and Legal Power',
    text: 'The strategy enforces the Unitary Executive Theory, placing independent federal agencies—including the Department of Justice—under direct presidential control. It eliminates civil service protections for thousands of employees to allow political appointments, restricts legal immigration and asylum pathways, and coordinates mass detention and deportation protocols, including proposals that affect the Department of Homeland Security.',
  },
  {
    title: 'Labor and Economic Restructuring',
    text: 'The plan scales back workplace and union protections, allows corporations to narrow overtime eligibility, reduces federal oversight of workplace safety, and cuts federal job-support initiatives. It also targets organized labor through union decertification, ending project labor agreements, and reducing public-sector union power.',
  },
  {
    title: 'Reduction of Social Safety Nets and Education',
    text: 'The framework restricts federal food assistance, implements deep cuts or benefit caps affecting Medicaid and Medicare, dissolves early childhood initiatives such as Head Start, phases out income-driven student loan repayment plans, eliminates Public Service Loan Forgiveness, and dismantles the Department of Education.',
  },
  {
    title: 'Restructuring Telecommunications and Public Media Platforms',
    text: 'The plan reorients the federal media and technology apparatus by defunding public broadcasting infrastructure—such as the Corporation for Public Broadcasting, NPR, and PBS—and stripping them of their noncommercial, educational legal status.',
  },
  {
    title: 'Healthcare and Reproductive Rights',
    text: 'The plan halts federal efforts aimed at lowering prescription drug prices, encourages a shift toward private Medicare options, and restricts access to reproductive healthcare, including proposals involving federal agency directives and enforcement of the Comstock Act.',
  },
  {
    title: 'Environmental and Climate Deregulation',
    text: 'The operational strategy strips away federal climate initiatives, reduces renewable energy funding, weakens pollution protections, downsizes the National Oceanic and Atmospheric Administration, and reduces independent civil-service infrastructure that manages public resources.',
  },
  {
    title: 'Taxation and Corporate Reform',
    text: 'The economic strategy implements large-scale corporate and personal tax cuts while reducing federal regulation of private enterprise. It condenses individual income tax brackets down to a simplified two-rate tier system (15% and 30%), reduces the corporate tax rate from 21% to 18%, and lowers capital gains taxes. Operationally, the plan eliminates the Consumer Financial Protection Bureau, downizes antitrust enforcement, and seeks a long-term transition from income-based taxation toward a national consumption tax framework.',
  },
  {
    title: 'Elimination of Diversity Mandates and Equity Frameworks',
    text: 'The plan executes a systemic extraction of diversity, equity, and inclusion (DEI) frameworks by purging related terminology from all federal rules, regulations, contracts, and grants. It abolishes dedicated DEI offices and personnel within the military, healthcare, and financial sectors. Operationally, the model halts federal workforce demographic data collections, narrows civil rights enforcement by eliminating the legal standard of "disparate impact," and mandates the immediate termination of federal employees who participate in equity initiatives.',
  },
  {
    title: 'Expanding Warrantless Surveillance and Domestic Intelligence Powers',
    text: 'The plan expands the executive branch\'s authority to utilize electronic intelligence gathering and surveillance tools by dismantling existing administrative guardrails on domestic spying. It reorients the federal security apparatus to allow the deployment of military personnel for domestic law enforcement, increases executive authority over journalists and public demonstrations, and restricts independent oversight within the intelligence community.',
  },
];

const historyTagTooltips = [
  {
    term: 'Black Wall Street Massacre',
    definition: (
      <>
        A 1921 attack by white mobs on Tulsa&apos;s prosperous Greenwood District, known as Black Wall
        Street, destroying homes and businesses and killing many Black residents.
      </>
    ),
    href: 'https://www.tulsahistory.org/exhibit/1921-tulsa-race-massacre/',
  },
  {
    term: 'Lynchings',
    definition: (
      <>
        Public acts of mob violence, often murder, used especially during the racial terror era to
        intimidate Black communities and enforce white supremacy.
      </>
    ),
    href: 'https://lynchinginamerica.eji.org/',
  },
  {
    term: 'Civil rights denied',
    definition: (
      <>
        The refusal to protect equal rights in voting, public accommodations, education, employment,
        and other areas of public life.
      </>
    ),
    href: 'https://www.archives.gov/milestone-documents/civil-rights-act',
  },
  {
    term: 'Unjust treatment',
    definition: (
      <>
        Unequal, biased, or abusive treatment by people, institutions, or legal systems, especially
        when protection and accountability are denied.
      </>
    ),
    href: 'https://www.justice.gov/crt',
  },
  {
    term: 'Dehumanization',
    definition: (
      <>
        Treating people as though they are less than fully human, which can make cruelty, exclusion,
        and violence easier to justify.
      </>
    ),
    href: 'https://www.britannica.com/dictionary/dehumanize',
  },
  {
    term: 'Violence without prosecution',
    definition: (
      <>
        Violent acts that are not criminally charged or punished, leaving victims without legal
        accountability or public justice.
      </>
    ),
    href: 'https://www.justice.gov/crt/page/file/1569361/dl',
  },
];

const racismTooltip = (
  <>
    Prejudice, discrimination, or unequal treatment toward people based on race or ethnicity. It is
    the belief that one race is superior to another.
  </>
);

export function ClarityPage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = `Clarity | ${appConfig.siteName}`;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="page clarity-page">
      <section className="clarity-hero" aria-labelledby="clarity-title">
        <p id="clarity-title" className="clarity-hero-intro">
          This is a reflection on how deeply held beliefs, cultural concerns, and organized political
          efforts have become connected into a powerful political force. It asks readers to consider
          what that force is advancing and whether it reflects the values they want to stand with.
        </p>

        <div className="clarity-choice-band" aria-label="Two framing questions">
          {clarityFramingQuestions.map((question) => (
            <div key={question} className="clarity-choice-pill">
              {question}
            </div>
          ))}
        </div>
      </section>

      <section className="clarity-view-grid" aria-label="Christian viewpoints">
        <article className="clarity-view-card clarity-view-card--primary">
          <span className="clarity-label">Christian Nationalist view</span>
          <h3>Moral law enforcement</h3>
          <p>
            Many Christians believe they are morally obligated to use every available legal and
            political means, including elections and judicial appointments, to oppose abortion
            and uphold what they understand to be God’s design for sexuality.
          </p>
        </article>

        <article className="clarity-view-card clarity-view-card--opposing">
          <span className="clarity-label">Opposing Christian view</span>
          <h3>Command with personal choice</h3>
          <p>
            God has given commands and revealed His will, yet He still allows each
            person the choice to believe, obey, and follow Him willingly.
          </p>
        </article>
      </section>

      <section className="clarity-history-panel" aria-labelledby="history-title">
        <div className="clarity-history-copy">
          <span className="clarity-label">Historical context</span>
          <h2 id="history-title">
            Civil rights and the political force that is enabling our current issues
          </h2>

          <p>
            The Civil Rights Act of 1964 became a major dividing line in American politics because it
            challenged the legal and social order that had protected segregation for generations. For
            many Black Americans, the law represented long-delayed federal protection in public
            accommodations, employment, education, voting access, and basic dignity.
          </p>

          <p>
            President Lyndon B. Johnson is often associated with the warning that Democrats had
            &ldquo;lost the South for a generation&rdquo; after supporting civil rights. The point was
            not that Johnson opposed civil rights because of the political cost. Rather, he understood
            that supporting federal civil rights protections would likely alienate many white Southern
            voters who opposed integration, yet he still pushed hard for the legislation.
          </p>

          <p>
            That history helps explain why civil rights became tied to larger arguments about
            &ldquo;states&apos; rights,&rdquo; &ldquo;law and order,&rdquo; federal power, cultural
            change, and religious identity. Some Americans saw civil rights as a moral correction to
            injustice. Others saw it as a threat to the social order they wanted to preserve.
          </p>

          <div className="clarity-history-modern-terms" aria-label="Modern political terms context">
            <p>
              In today&apos;s debates, terms like{' '}
              <TooltipTerm term="DEI" title="Diversity, Equity, and Inclusion" href="https://www.merriam-webster.com/dictionary/DEI">
                A set of policies or efforts intended to encourage participation, fairness, and
                inclusion for equally qualified(capable) people from different backgrounds, identities,
                or groups in workplaces, schools, and organizations.
              </TooltipTerm>
              ,{' '}
              <TooltipTerm term="CRT" title="Critical Race Theory" href="https://www.britannica.com/topic/critical-race-theory">
                An academic framework that studies how racism and racial inequality has historically
                been reflected in laws, institutions, and society, not just in individual attitudes or
                actions.
              </TooltipTerm>
              ,{' '}
              <TooltipTerm term="woke" title="Woke" href="https://www.merriam-webster.com/dictionary/woke">
                Originally a term meaning being socially aware, especially about injustice or
                discrimination. Today it is often used more broadly—sometimes positively and sometimes
                critically—to describe strong attention to social justice or progressive social issues.
              </TooltipTerm>
              , and{' '}
              <TooltipTerm term="racism" title="Racism" href="https://www.britannica.com/topic/racism">
                {racismTooltip}
              </TooltipTerm>{' '}
              are often used when people argue about whether racial inequality should be addressed
              through schools, workplaces, laws, public policy, or personal responsibility.
            </p>
          </div>

          <div className="clarity-history-explainer" aria-label="Expanded civil rights context">
            <div className="clarity-history-explainer-item">
              <strong>Political cost</strong>
              <p>
                Supporting civil rights carried real electoral risk because many of those who supported
                segregation, including Christian segregationist, resisted federal intervention and
                shifted their political loyalty over time to the Republican Party.
              </p>
            </div>

            <div className="clarity-history-explainer-item">
              <strong>Moral urgency</strong>
              <p>
                Civil rights supporters believed the nation could not honestly claim liberty while
                allowing segregation, racial intimidation, unequal treatment, and denial of basic rights.
              </p>
            </div>

            <div className="clarity-history-explainer-item">
              <strong>Religious division</strong>
              <p>
                American Christians were not united. Some defended or tolerated segregation and{' '}
                <TooltipTerm term="racism" title="Racism" href="https://www.britannica.com/topic/racism">
                  {racismTooltip}
                </TooltipTerm>
                , while others argued that racial equality and integration were consistent with the
                teachings of Jesus, human dignity, and neighborly love.
              </p>
            </div>

            <div className="clarity-history-explainer-item">
              <strong>Long memory of injustice</strong>
              <p>
                For many individuals, the determination can be understood against a painful history
                that included segregation, the Black Wall Street Massacre, lynchings, denied civil
                rights, dehumanization, unjust treatment, and violent crimes that often went unpunished.
              </p>
            </div>
          </div>

          <ul className="clarity-history-tags" aria-label="Examples of historical injustices">
            {historyTagTooltips.map((item) => (
              <li key={item.term}>
                <TooltipTerm term={item.term} title="Brief definition" href={item.href}>
                  {item.definition}
                </TooltipTerm>
              </li>
            ))}
          </ul>
        </div>

        <aside className="clarity-turning-point-card" aria-label="Political efforts and sources">
          <strong>Political efforts</strong>
          <p>
            The political response to civil rights became a major turning point in American party
            alignment and public messaging. Over time, organizations and movements such as the Moral
            Majority, The Heritage Foundation, Turning Point USA, and Project 2025 helped shape a more
            united political force around deeply held religious beliefs, cultural preferences, policy
            goals, and political identity.
          </p>

          <p className="clarity-effort-note">
            Hover or tap each item for a brief explanation and a further-reading link.
          </p>

          <div className="clarity-source-links">
            <SourceWithTooltip
              label="Southern Strategy"
              href="https://www.britannica.com/topic/Southern-strategy"
              definition={
                <>
                  A political strategy associated mainly with the late 20th century in which Republicans
                  sought greater support from white Southern voters, especially after the civil rights
                  era, often by emphasizing issues such as states&apos; rights, law and order, and
                  opposition to certain federal policies.
                </>
              }
            />
            <SourceWithTooltip
              label="The Heritage Foundation"
              href="https://www.heritage.org/"
              definition={
                <>
                  A conservative policy organization that has influenced Republican policy priorities,
                  judicial philosophy, federal agency proposals, and government reform plans.
                </>
              }
            />
            <SourceWithTooltip
              label="Turning Point USA"
              href="https://tpusa.com/"
              definition={
                <>
                  A conservative youth and campus organization that promotes conservative political
                  ideas, trains activists, and works to influence young voters and public messaging.
                </>
              }
            />
            <SourceWithTooltip
              label="Moral Majority"
              href="https://en.wikipedia.org/wiki/Moral_Majority"
              definition={
                <>
                  A late-20th-century religious conservative political movement that helped mobilize
                  evangelical voters around abortion, family values, school prayer, and opposition to
                  cultural liberalism.
                </>
              }
            />
          </div>
        </aside>
      </section>

      <section className="clarity-project">
        <div className="clarity-project-header">
          <span className="clarity-label">Policy framework</span>
          <h2>Project 2025: An Operational Plan for Government Restructuring</h2>
          <span className="clarity-project-subtitle">
            <a
              href="https://static.project2025.org/2025_MandateForLeadership_FULL.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              2025_MandateForLeadership_FULL.pdf
            </a>
          </span>
        </div>

        <section className="clarity-project-content" aria-label="Project 2025 details">
          <p className="clarity-project-intro">
            Project 2025 operates as a highly coordinated blueprint designed to reshape the federal
            government, centralize executive power, and dismantle existing social safety nets. The
            core operational mechanism relies on executing sweeping changes through direct executive
            action, including reclassifying tens of thousands of civil service positions so they
            can be filled by political loyalists.
          </p>

          <div className="clarity-pillar-grid">
            {projectPillars.map((pillar) => (
              <article key={pillar.title} className="clarity-pillar-card">
                <h3>{pillar.title}</h3>
                <p>{pillar.text}</p>
              </article>
            ))}
          </div>
        </section>
      </section>

      <ClarityTopicNav />
    </div>
  );
}
